import React from 'react';
import { useNavigate } from 'react-router-dom';
import Joyride, { CallBackProps, STATUS, Step, TooltipRenderProps } from 'react-joyride';
import styled, { keyframes, css } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { Rocket, Building2, Bell, Sparkles, ArrowRight, X, XCircle } from 'lucide-react';
import { useTutorialStore, TUTORIAL_ROUTES, TUTORIAL_PAGE_NAMES } from '@/store/useTutorialStore';

interface MySpaceTutorialProps {
  run: boolean;
  onComplete: () => void;
}

/**
 * 마이스페이스 튜토리얼 스텝 정의
 */
const steps: Step[] = [
  {
    target: 'body',
    content: 'welcome',
    placement: 'center',
    disableBeacon: true,
    disableOverlay: false,
  },
  {
    target: '[data-tutorial="hero-section"]',
    content: 'hero',
    placement: 'bottom',
    disableBeacon: true,
    spotlightPadding: 0,
  },
  {
    target: '[data-tutorial="company-section"]',
    content: 'company',
    placement: 'right',
    disableBeacon: true,
    spotlightPadding: 8,
  },
  {
    target: '[data-tutorial="notification-section"]',
    content: 'notification',
    placement: 'left',
    disableBeacon: true,
    spotlightPadding: 8,
  },
];

/**
 * 스텝별 컨텐츠 정의
 */
const stepContents: Record<string, { icon: React.ReactNode; title: string; description: string }> = {
  welcome: {
    icon: <Sparkles size={32} />,
    title: '팩트시트에 오신 것을 환영합니다!',
    description: '팩트시트는 스타트업, 투자사, 액셀러레이터 프로그램을 위한 통합 플랫폼입니다.\n\n간단한 가이드를 통해 주요 기능을 알아보세요!',
  },
  hero: {
    icon: <Rocket size={28} />,
    title: '마이스페이스',
    description: '이곳은 여러분의 개인 대시보드입니다.\n소속된 회사와 알림을 한눈에 확인할 수 있습니다.',
  },
  company: {
    icon: <Building2 size={28} />,
    title: '내 공간',
    description: '소속된 회사 목록이 여기에 표시됩니다.\n\n각 회사 카드를 클릭하면 해당 회사의 서비스 공간으로 이동할 수 있습니다.\n- 스타트업: 투자 유치 및 기업 관리\n- 투자사: 포트폴리오 및 딜 관리\n- 선발 프로그램: 프로그램 운영 관리',
  },
  notification: {
    icon: <Bell size={28} />,
    title: '알림 센터',
    description: '중요한 업데이트와 알림을 확인할 수 있습니다.\n\n새로운 초대, 요청 승인, 문서 업데이트 등 다양한 알림을 받아보세요.',
  },
};

/**
 * 커스텀 툴팁 컴포넌트
 */
const CustomTooltip: React.FC<TooltipRenderProps> = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  tooltipProps,
  isLastStep,
  size,
}) => {
  const navigate = useNavigate();
  const { completeTutorialAndGoNext, completeTutorial } = useTutorialStore();
  const content = stepContents[step.content as string];
  const isWelcome = step.content === 'welcome';
  
  // localStorage에서 온보딩 플로우 여부 직접 확인
  const isOnboarding = localStorage.getItem('factsheet_onboarding_flow') === 'true';

  // 다음 페이지로 이동
  const handleNavigateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    closeProps.onClick(e);
    const nextPage = completeTutorialAndGoNext();
    if (nextPage) {
      navigate(TUTORIAL_ROUTES[nextPage] + '?tutorial=true');
    }
  };

  // 튜토리얼 닫기 (완료 처리)
  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    closeProps.onClick(e);
    completeTutorial('my-space');
  };

  return (
    <TooltipContainer {...tooltipProps} $isWelcome={isWelcome}>
      <CloseButton {...closeProps}>
        <X size={18} />
      </CloseButton>
      
      <TooltipHeader $isWelcome={isWelcome}>
        <IconWrapper $isWelcome={isWelcome}>
          {content.icon}
        </IconWrapper>
        <TooltipTitle>{content.title}</TooltipTitle>
      </TooltipHeader>
      
      <TooltipContent>
        {content.description.split('\n').map((line, i) => (
          <p key={i}>{line || <br />}</p>
        ))}
      </TooltipContent>
      
      <TooltipFooter>
        <StepIndicator>
          {Array.from({ length: size }, (_, i) => (
            <StepDot key={i} $active={i === index} />
          ))}
        </StepIndicator>
        
        <ButtonGroup>
          {index > 0 && (
            <BackButton {...backProps}>
              이전
            </BackButton>
          )}
          {isLastStep ? (
            <>
              <CloseButtonStyled onClick={handleCloseClick}>
                <XCircle size={16} /> 닫기
              </CloseButtonStyled>
              {isOnboarding && (
                <PrimaryButton onClick={handleNavigateClick}>
                  회사 관리로 이동 <ArrowRight size={16} />
                </PrimaryButton>
              )}
            </>
          ) : (
            <PrimaryButton {...primaryProps}>
              다음 <ArrowRight size={16} />
            </PrimaryButton>
          )}
        </ButtonGroup>
      </TooltipFooter>
    </TooltipContainer>
  );
};

/**
 * 마이스페이스 튜토리얼 컴포넌트
 */
const MySpaceTutorial: React.FC<MySpaceTutorialProps> = ({ run, onComplete }) => {
  const { stopTutorial } = useTutorialStore();
  
  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    
    if (finishedStatuses.includes(status)) {
      onComplete();
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress={false}
      showSkipButton
      scrollToFirstStep
      disableOverlayClose={false}
      spotlightClicks
      spotlightPadding={12}
      callback={handleJoyrideCallback}
      tooltipComponent={CustomTooltip}
      floaterProps={{
        styles: {
          floater: {
            filter: 'drop-shadow(0 10px 40px rgba(0, 0, 0, 0.2))',
          },
        },
      }}
      styles={{
        options: {
          arrowColor: '#fff',
          backgroundColor: '#fff',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
          primaryColor: '#6366f1',
          zIndex: 10000,
        },
        spotlight: {
          borderRadius: 16,
        },
      }}
    />
  );
};

// ============================================
// Animations
// ============================================

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// ============================================
// Styled Components
// ============================================

const TooltipContainer = styled.div<{ $isWelcome?: boolean }>`
  position: relative;
  background: #fff;
  border-radius: 20px;
  padding: 24px;
  min-width: ${props => props.$isWelcome ? '420px' : '340px'};
  max-width: 440px;
  animation: ${fadeIn} 0.3s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  
  ${props => props.$isWelcome && `
    text-align: center;
    padding: 32px;
  `}
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: ${semanticColors.background.tertiary};
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.secondary};
    color: ${semanticColors.text.primary};
  }
`;

const TooltipHeader = styled.div<{ $isWelcome?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
  
  ${props => props.$isWelcome && `
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
  `}
`;

const IconWrapper = styled.div<{ $isWelcome?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${props => props.$isWelcome ? '72px' : '52px'};
  height: ${props => props.$isWelcome ? '72px' : '52px'};
  border-radius: 16px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  flex-shrink: 0;
  
  ${props => props.$isWelcome && css`
    animation: ${pulse} 2s ease-in-out infinite;
  `}
`;

const TooltipTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const TooltipContent = styled.div`
  font-size: 0.9375rem;
  line-height: 1.7;
  color: ${semanticColors.text.secondary};
  margin-bottom: 20px;
  
  p {
    margin: 0;
  }
`;

const TooltipFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid ${semanticColors.border.primary};
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 6px;
`;

const StepDot = styled.div<{ $active: boolean }>`
  width: ${props => props.$active ? '20px' : '8px'};
  height: 8px;
  border-radius: 100px;
  background: ${props => props.$active ? '#6366f1' : semanticColors.background.tertiary};
  transition: all 0.3s ease;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const BackButton = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid ${semanticColors.border.primary};
  background: #fff;
  color: ${semanticColors.text.secondary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.tertiary};
    border-color: ${semanticColors.border.secondary};
  }
`;

const CloseButtonStyled = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 10px;
  border: 1px solid ${semanticColors.border.primary};
  background: #fff;
  color: ${semanticColors.text.secondary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.tertiary};
    border-color: ${semanticColors.border.secondary};
    color: ${semanticColors.text.primary};
  }
`;

const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
  }
`;

export default MySpaceTutorial;
